// 에피소드 진단 상세의 값 구간(`segments`) wire 경계다.
//
// action_source 채널의 구간 목록은 4-상태다. 이 파일은 이 상태를 구분하려고 있다:
//   - none:    데이터셋에 그 채널이 없다. 그래서 구간이 생길 수 없다.
//   - pending: 채널은 있지만 이 에피소드의 구간이 아직 없다(재진단 전). 구 BE 가 필드를
//              보내지 않는 배포 기간도 이 상태다.
//   - unknown: 목록이 비었고 채널 구조를 아직 모른다(구조 로딩 중·실패). 없음과 대기를
//              구분할 근거가 없다.
//   - ready:   구간이 있다.
// BE 는 "그 채널이 없음"과 "아직 측정 안 함"을 똑같이 빈 목록으로 보낸다. 둘을 같은 문구로
// 표시하면 채널이 없는 데이터셋에 "곧 준비된다"고 표시하게 된다. 판정은 이 파일의 헬퍼로만 한다.
//
// Owns:
// - action_source 채널 이름과 4-상태 판정(구간 목록 + 데이터셋 채널 구조).
// - 배포 기간 호환: 필드가 없으면(구 BE) 빈 목록으로 읽는다.
//
// Does not own:
// - 구간을 화면 띠로 합치는 규칙(ZOH·병합). `diagnostics/segments/actionSourceSegments` 가 소유한다.
// - 채널 구조 조회(`getDatasetStructure`).

import type { ChannelSegmentsResp, EpisodeDiagnosticDetailResp } from "./diagnostics";
import type { DatasetStructureChannel } from "./datasetStructure";

/** 행동 출처 채널이다. 이 채널에는 `ActionSource`(TELEOP·INFERENCE·INTERVENTION·SCRIPT) 정수를 기록한다. */
export const ACTION_SOURCE_CHANNEL = "auxiliary_vector::action_source";

export type ActionSourceSegmentsState =
  | { kind: "none" }
  | { kind: "pending" }
  | { kind: "unknown" }
  | { kind: "ready"; channel: ChannelSegmentsResp };

/**
 * 상세 응답의 구간 목록이다. 필드가 없으면 빈 목록을 반환한다. 구 BE 는 이 필드를 보내지 않는다.
 * `actionSourceSegmentsState` 가 빈 목록을 채널 구조와 함께 pending 으로 읽는다. 그래서 뜻이
 * 사라지지 않는다.
 */
export function episodeSegmentsOf(
  detail: Pick<EpisodeDiagnosticDetailResp, "segments">,
): readonly ChannelSegmentsResp[] {
  // @compat ZERG-299 until=be:a25ad232
  return detail.segments ?? [];
}

/**
 * 데이터셋 채널 구조에 action_source 가 있는지 판정한다. 구조를 아직 모르면(undefined) null 을
 * 반환한다. 없음과 모름을 섞지 않는다.
 */
export function datasetHasActionSource(
  channels: readonly DatasetStructureChannel[] | undefined,
): boolean | null {
  if (channels === undefined) {
    return null;
  }
  return channels.some((channel) => `${channel.topic}::${channel.name}` === ACTION_SOURCE_CHANNEL);
}

/**
 * 구간 목록과 데이터셋 채널 구조로 4-상태를 판정한다.
 *
 * 구간이 있으면 구조와 관계없이 ready 다. 구간이 있으면 채널도 있다.
 * 구간 목록이 비었을 때만 구조로 없음·대기·모름을 구분한다.
 */
export function actionSourceSegmentsState(
  segments: readonly ChannelSegmentsResp[],
  datasetHasChannel: boolean | null,
): ActionSourceSegmentsState {
  const channel = segments.find((entry) => entry.channel === ACTION_SOURCE_CHANNEL);
  if (channel !== undefined && channel.segments.length > 0) {
    return { kind: "ready", channel };
  }
  if (datasetHasChannel === null) {
    return { kind: "unknown" };
  }
  return datasetHasChannel ? { kind: "pending" } : { kind: "none" };
}
